import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Fish } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { scrollToId, scrollTop } from "@/hooks/useLenis";

const links = [
  { id: "products", key: "products" },
  { id: "manifesto", key: "manifesto" },
  { id: "pricing", key: "pricing" },
  { id: "demo", key: "demo" },
  { id: "contact", key: "contact" },
];

export default function Header() {
  const { t, lang, setLanguage } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-2xl bg-ink-bg/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button onClick={scrollTop} className="flex items-center gap-2.5 group" data-testid="logo-home">
          <span className="grid place-items-center w-9 h-9 rounded-full border border-cyan-brand/40 text-cyan-brand group-hover:bg-cyan-brand/10 transition-colors duration-200">
            <Fish size={18} strokeWidth={1.8} />
          </span>
          <span className="font-display font-extrabold tracking-tight text-lg text-white leading-none">
            SOR<span className="text-cyan-brand">.</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-${l.id}`}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-cyan-brand transition-colors duration-200"
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-white/10 p-0.5 font-mono text-[11px]" data-testid="lang-switcher">
            {["it", "de"].map((l) => (
              <button
                key={l}
                onClick={() => setLanguage(l)}
                data-testid={`lang-${l}`}
                className={`px-2.5 py-1 rounded-full uppercase tracking-wider transition-colors duration-200 ${
                  lang === l ? "bg-cyan-brand text-ink-bg font-semibold" : "text-white/55 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <button
            onClick={() => go("contact")}
            data-testid="header-cta"
            className="hidden md:inline-flex items-center rounded-full bg-white text-ink-bg font-semibold text-sm px-5 py-2.5 hover:bg-cyan-brand transition-colors duration-200 active:scale-95"
          >
            {t.nav.cta}
          </button>

          <button className="lg:hidden text-white p-1" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-ink-bg/95 backdrop-blur-2xl border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} className="text-left font-display text-2xl text-white" data-testid={`mobile-nav-${l.id}`}>
                  {t.nav[l.key]}
                </button>
              ))}
              <button onClick={() => go("contact")} className="mt-2 rounded-full bg-cyan-brand text-ink-bg font-semibold py-3">
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
